/**
 * UserLocationMarker - Custom user location indicator
 */

import { Marker } from 'react-map-gl';
import { useLocationStore, useAirQualityStore, selectNeuralIndex } from '@/stores';
import { cn } from '@/utils/cn';
import type { Coordinates } from '@/types';

interface UserLocationMarkerProps {
  coordinates: Coordinates;
}

export function UserLocationMarker({ coordinates }: UserLocationMarkerProps) {
  const heading = useLocationStore((state) => state.heading);
  const neuralIndex = useAirQualityStore(selectNeuralIndex);

  // Determine pulse color based on neural index
  const pulseColor =
    neuralIndex >= 80 ? 'var(--hud-success)' :
    neuralIndex >= 50 ? 'var(--hud-warning)' :
    'var(--hud-danger)';

  return (
    <Marker
      longitude={coordinates.lng}
      latitude={coordinates.lat}
      anchor="center"
    >
      <div className="relative">
        {/* Outer pulse ring */}
        <div
          className="absolute -inset-4 rounded-full animate-ping"
          style={{
            backgroundColor: pulseColor,
            opacity: 0.3,
            animationDuration: '2s',
          }}
        />

        {/* Middle glow ring */}
        <div
          className="absolute -inset-2 rounded-full"
          style={{
            backgroundColor: pulseColor,
            opacity: 0.2,
            boxShadow: `0 0 20px ${pulseColor}`,
          }}
        />

        {/* Main marker */}
        <div
          className={cn(
            'relative w-6 h-6 rounded-full',
            'border-2 border-white',
            'flex items-center justify-center',
            'shadow-lg'
          )}
          style={{
            backgroundColor: pulseColor,
            boxShadow: `0 0 15px ${pulseColor}`,
          }}
        >
          {/* Heading indicator */}
          {heading !== null && (
            <div
              className="absolute -top-3 w-0 h-0"
              style={{
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderBottom: `8px solid ${pulseColor}`,
                transform: `rotate(${heading}deg)`,
                transformOrigin: 'center 15px',
              }}
            />
          )}

          {/* Inner dot */}
          <div className="w-2 h-2 rounded-full bg-white" />
        </div>

        {/* Neural index mini badge */}
        <div
          className={cn(
            'absolute -bottom-6 left-1/2 -translate-x-1/2',
            'px-1.5 py-0.5 rounded',
            'text-[10px] font-data font-bold',
            'bg-glass-bg backdrop-blur-sm border border-glass-border',
            'whitespace-nowrap'
          )}
          style={{ color: pulseColor }}
        >
          NI: {neuralIndex}
        </div>
      </div>
    </Marker>
  );
}

export default UserLocationMarker;
